import { useEffect } from 'react';
import { type RouteObject, useLocation } from 'react-router-dom';

import { type SessionValue, useSession } from '@/auth/SessionProvider';
import {
  type UsageIdentity,
  setUsageRoutes,
  syncUsageSession,
  trackUsage,
  trackUsagePageView,
} from '@/usage/client';
import { routeRecord } from '@/usage/records';
import { routePattern } from '@/usage/scrub';

const positiveInt = (value: unknown): number | null => {
  const n = Number(value);
  return Number.isInteger(n) && n > 0 ? n : null;
};

/**
 * What usage analytics may know about the session: the user id, the active
 * role assignment id and its organisation (id and type). null until a user is
 * signed in AND the server confirmed the active role. Never the user's name or
 * e-mail, never the assignment's label (it holds the organisation's name).
 */
export const usageIdentityOf = (
  session: Pick<SessionValue, 'status' | 'user' | 'assignment'>,
): UsageIdentity | null => {
  const userId = positiveInt(session.user?.id);
  const assignmentId = positiveInt(session.assignment?.id);
  if (session.status !== 'signed-in' || userId === null || assignmentId === null) return null;
  return {
    userId,
    assignmentId,
    organisationId: positiveInt(session.assignment?.contextId),
    organisationType: String(session.assignment?.contextType ?? ''),
  };
};

const usage = { track: trackUsage };

/**
 * Usage analytics for the rest of the app:
 *
 *   const { track } = useUsage();
 *   track('profile_tab_opened', { record_type: 'brand', record_id: 12, tab: 'scouting' });
 *
 * Event names and property names are the typed lists of usage/events.ts (ids,
 * enum values, counts and booleans only). Does nothing while usage analytics
 * is switched off or nobody is signed in.
 */
export const useUsage = (): { track: typeof trackUsage } => usage;

/**
 * Mounted once inside the router, only when usage analytics is switched on
 * (src/routes RootLayout). Follows the session (identify on sign-in, reset on
 * sign-out and on a role switch) and sends one page view per route change,
 * with the route pattern instead of the address and, on a detail page, the
 * record's type and numeric id (usage/records.ts). Renders nothing.
 */
export const UsageTracker = ({ routes }: { routes: RouteObject[] }) => {
  const session = useSession();
  const { pathname } = useLocation();
  const identity = usageIdentityOf(session);
  const { userId, assignmentId, organisationId, organisationType } = identity ?? {};

  useEffect(() => {
    setUsageRoutes(routes);
  }, [routes]);

  useEffect(() => {
    syncUsageSession(
      userId && assignmentId
        ? {
            userId,
            assignmentId,
            organisationId: organisationId ?? null,
            organisationType: organisationType ?? '',
          }
        : null,
    );
  }, [userId, assignmentId, organisationId, organisationType]);

  // after the session effect above: the first page view of a session follows its identify
  useEffect(() => {
    if (userId && assignmentId) {
      trackUsagePageView(routePattern(routes, pathname), routeRecord(routes, pathname));
    }
  }, [userId, assignmentId, routes, pathname]);

  return null;
};
